import { Action } from '../core/action'
import { DeepState, State, Value } from '../core/state'
import type { OSSDK } from '../sdk'

export type TemplateSettings = {
  version: string
  name: string
  designConfig: any
}

type onCheckoutElementActionArgs = {
  elementData: any
  proposalData: any
  actionData: any
}

type AlteredDesignType = {
  [systemUuid: string]: {
    code: string
    delta: number
  }[]
}

/**
 * Argument for {@link proposal.hydrateForPdf}. A single flat object so the shape can grow without
 * churning the call signature. The selected system/payment option is already baked into
 * `proposalData` server-side (Django pre-selects), so the app reads selection from
 * `proposalData.selectedSystem` / `selectedPaymentOption`. `systemUuid` / `paymentOptionId` remain
 * as optional, informational echoes of the pro's selection and are not consumed by the SDK.
 */
export type HydrateForPdfArgs = {
  // Finished ProposalDataTypeV2 built server-side by Django (NOT a raw API response).
  proposalData: Partial<Record<string, any>>
  systemUuid?: string
  paymentOptionId?: number
}

export type ContractTemplateOverridesUpdatePayload = {
  systemUuid: string
  paymentOptionId: number
  contractId: number | null
}

export type ContractTemplateModeChangePayload = {
  mode: 'project' | 'payment-option'
  systemUuids: string[]
}

export const proposal = {
  // State
  annotations: new DeepState<Partial<Record<string, any>>>({}),
  checkoutElements: new DeepState<Partial<Record<string, any>>>({}),
  data: new State<Partial<Record<string, any>>>({}),
  isDiscarding: new Value<boolean>(false),
  isViewAsCustomer: new Value<boolean>(false),
  /** True only while the screenshot service is rendering this proposal to PDF (set in hydrateForPdf). */
  isViewingAsPDF: new Value<boolean>(false),
  /** Polled by the screenshot service for Proposal V2 URL+hydrate PDFs (`isPdfRenderingReady.value`). */
  isPdfRenderingReady: new Value<boolean>(false),
  lenderLogoPaths: new DeepState<Partial<Record<string, any>>>({}),
  loadedTemplateSettings: new DeepState<Partial<TemplateSettings>>({}),
  permissions: new DeepState<Partial<Record<string, any>>>({}),
  loadingSystemSatelliteImages: new DeepState<string[]>([]),
  storedSystemSatelliteImages: new DeepState<Partial<Record<string, string>>>({}),
  sendButtonOptions: new DeepState<Partial<Record<string, any>>>({}),
  submitting: new Value<boolean>(false),
  templateSettings: new DeepState<Partial<TemplateSettings>>({}),
  transaction: new DeepState<Partial<Record<string, any>>>({}),
  viewportDevice: new Value<string>('current'),
  genericState: new DeepState<Partial<Record<string, any>>>({}),

  // Actions
  onCheckoutElementAction: new Action<[arg: onCheckoutElementActionArgs], unknown>(),
  onComponentQuantityChange: new Action<[alteredDesign: AlteredDesignType]>(),
  onContractTemplateOverridesUpdate: new Action<[ContractTemplateOverridesUpdatePayload]>(),
  onContractTemplateModeChange: new Action<[ContractTemplateModeChangePayload]>(),
  onPaymentMethodChange: new Action<[id: number]>(),
  onProposalTemplateChange: new Action<[rawSettings: TemplateSettings], any>(),
  onSaveTemplateSettings: new Action<[rawSettings: TemplateSettings], any>(),
  onSendProposal: new Action(),
  onShowDialog: new Action<[dialogName: string, dialogData?: any], void>(),
  onSystemChange: new Action<[uuid: string]>(),
  onUploadImage: new Action<[files: File[]], string | undefined>(),
  resetDiscardTemplateSettings: new Action(),
  setIsEditorActive: new Action<[value: boolean]>(),
  setIsViewAsCustomer: new Action<[value: boolean]>(),
  refreshData: new Action(),
  handleGenericActionCallbacks: new Action<[actionName: 'redirect', options?: any], void>(),

  /**
   * PDF rendering entry point. The screenshot service does NOT call this directly; it drives the
   * stable flat window contract instead — `window.osProposal_injectPdfProposalData(arg)` once
   * `window.osProposal_isInitialised` is true (see {@link promoteProposalPdfWindowApi}). This method
   * is the default implementation behind that window hook.
   *
   * It plays the same role the host SPA's `useBindProposal` plays online (master side): it
   * ASSIGNS the proposal state so the connected-mode render path runs. There is no host app in
   * the PDF context, so Puppeteer triggers it.
   *
   * IMPORTANT: this is a pure state-passer — it does NOT compute or reshape anything. Django
   * delivers a finished ProposalDataTypeV2 (see document_generator.proposal_v2_sdk_data) and this
   * method only assigns it and sets flags. It must NOT call `setConfig` (that throws
   * "SDK already initialized"); the app has already initialised the SDK.
   */
  hydrateForPdf(args: HydrateForPdfArgs) {
    assignProposalStateForPdf(args)
    takeOwnershipForPdfRendering()
    resetPdfRenderingReady()
  },

  /**
   * Called by the proposal app (via `window.ossdk.proposal.markPdfRenderingReady()`) when it
   * considers its render settled. This flips `isPdfRenderingReady`, which is mirrored to
   * `window.osProposal_renderReady` (see {@link promoteProposalPdfWindowApi}) — the flag the
   * screenshot service polls (with a max-timeout fallback). When/how an app decides it is ready is
   * intentionally app-specific.
   */
  markPdfRenderingReady() {
    proposal.isPdfRenderingReady.own()
    proposal.isPdfRenderingReady.value = true
  },
} as const

/** Assign proposal scope to match useBindProposal master-side fields (useBindProposal.ts:84-206). */
function assignProposalStateForPdf({ proposalData }: HydrateForPdfArgs) {
  proposal.data.value = proposalData

  const templateSettings = proposalData?.proposalTemplateSettings?.proposal_v2_config?.template_settings
  if (templateSettings) {
    proposal.templateSettings.value = templateSettings
  }

  proposal.permissions.value = {}
  proposal.isViewAsCustomer.value = true
  proposal.isViewingAsPDF.value = true
}

/** Slave app must own state so useSdkStateSelector subscribers receive updates. */
function takeOwnershipForPdfRendering() {
  proposal.data.own()
  proposal.templateSettings.own()
  proposal.permissions.own()
  proposal.isViewAsCustomer.own()
  proposal.isViewingAsPDF.own()
  proposal.genericState.own()
  proposal.isPdfRenderingReady.own()
}

/**
 * Reset the readiness flag on (re)hydrate so a stale `true` from a previous hydrate cannot leak. The
 * proposal app flips it true via `markPdfRenderingReady()` once it considers itself render-ready; the
 * SDK no longer guesses the timing with requestAnimationFrame.
 */
function resetPdfRenderingReady() {
  proposal.isPdfRenderingReady.own()
  proposal.isPdfRenderingReady.value = false
}

/**
 * Promote a thin, stable window-level PDF contract so the screenshot service (and any non-SDK
 * partner) can drive PDF rendering without depending on the SDK's internal object shape:
 *
 * - `window.osProposal_injectPdfProposalData(data)` — inject the proposal payload (wraps hydrateForPdf)
 * - `window.osProposal_isInitialised` — mirrors the SDK `isReady` state
 * - `window.osProposal_renderReady` — mirrors `proposal.isPdfRenderingReady`
 *
 * The SDK is the only thing populating these today; the methods/state behind them are the default
 * implementation of the window contract. Invoked once from init when the singleton is created.
 */
export function promoteProposalPdfWindowApi(sdk: OSSDK) {
  window.osProposal_injectPdfProposalData = (data: HydrateForPdfArgs) => sdk.proposal.hydrateForPdf(data)
  sdk.isReady.add(
    (ready: boolean) => {
      window.osProposal_isInitialised = ready
    },
    { now: true }
  )
  proposal.isPdfRenderingReady.add(
    (ready: boolean) => {
      window.osProposal_renderReady = ready
    },
    { now: true }
  )
}
